import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./useAuth";
import { apiRequest } from "@/lib/queryClient";

export interface Notification {
  id: number;
  recipientUserId: string;
  portalType: "broker" | "employer" | "employee";
  category: string;
  severity: "info" | "warning" | "urgent";
  title: string;
  message: string;
  ctaLabel?: string;
  ctaUrl?: string;
  relatedEntityType?: string;
  relatedEntityId?: number;
  status: "unread" | "read" | "archived";
  createdAt: string;
  readAt?: string;
}

interface UseNotificationsOptions {
  enabled?: boolean;
  refetchInterval?: number;
}

export function useNotifications(options: UseNotificationsOptions = {}) {
  const { enabled = true, refetchInterval = 30000 } = options;
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [wsConnected, setWsConnected] = useState(false);

  // Get current portal type
  const portalType = (() => {
    if (typeof window === "undefined") return "broker";
    const urlParams = new URLSearchParams(window.location.search);
    const roleFromUrl = urlParams.get("role");
    const savedRole = localStorage.getItem("selectedRole");
    const role = roleFromUrl || savedRole || "broker";
    if (role === "employer") return "employer";
    if (role === "employee") return "employee";
    return "broker";
  })();

  // Query for notifications list
  const {
    data: notifications = [],
    isLoading,
    error,
    refetch,
  } = useQuery<Notification[]>({
    queryKey: ["/api/notifications", { portalType }],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/notifications?portalType=${portalType}`);
      return res.json();
    },
    enabled: enabled && isAuthenticated,
    refetchInterval,
    refetchOnWindowFocus: true,
  });

  // Query for unread count
  const { data: unreadCountData } = useQuery<{ count: number }>({
    queryKey: ["/api/notifications/unread-count", { portalType }],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/notifications/unread-count?portalType=${portalType}`);
      return res.json();
    },
    enabled: enabled && isAuthenticated,
    refetchInterval,
  });

  const unreadCount = unreadCountData?.count ?? 0;

  // Mutation to mark a notification as read
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: number) => {
      const res = await apiRequest("PATCH", `/api/notifications/${notificationId}/read`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
    },
  });

  // Mutation to mark all notifications as read
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/notifications/mark-all-read`, { portalType });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
    },
  });

  // WebSocket connection for real-time notifications
  const connectWebSocket = useCallback(() => {
    if (!isAuthenticated || !user?.id) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = `${protocol}//${window.location.host}/ws`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log("[useNotifications] WebSocket connected");
        setWsConnected(true);
        // Authenticate with user ID for notifications
        ws.send(JSON.stringify({ type: "auth", userId: user.id }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === "notification") {
            // New notification received - update the cache optimistically
            queryClient.setQueryData<Notification[]>(
              ["/api/notifications", { portalType }],
              (old = []) => [data.notification, ...old]
            );
            queryClient.setQueryData<{ count: number }>(
              ["/api/notifications/unread-count", { portalType }],
              (old) => ({ count: (old?.count ?? 0) + 1 })
            );
          } else if (data.type === "auth_success") {
            console.log("[useNotifications] WebSocket authenticated");
          }
        } catch (err) {
          console.error("[useNotifications] Failed to parse WebSocket message:", err);
        }
      };

      ws.onclose = () => {
        console.log("[useNotifications] WebSocket disconnected");
        setWsConnected(false);
        wsRef.current = null;

        // Attempt to reconnect after 5 seconds
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        reconnectTimeoutRef.current = setTimeout(() => {
          if (isAuthenticated && user?.id) {
            connectWebSocket();
          }
        }, 5000);
      };

      ws.onerror = (error) => {
        console.error("[useNotifications] WebSocket error:", error);
      };
    } catch (err) {
      console.error("[useNotifications] Failed to create WebSocket:", err);
    }
  }, [isAuthenticated, user?.id, portalType, queryClient]);

  // Connect WebSocket when authenticated
  useEffect(() => {
    if (enabled && isAuthenticated && user?.id) {
      connectWebSocket();
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [enabled, isAuthenticated, user?.id, connectWebSocket]);

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    wsConnected,
    refetch,
    markAsRead: markAsReadMutation.mutate,
    markAllAsRead: markAllAsReadMutation.mutate,
    isMarkingAsRead: markAsReadMutation.isPending,
    isMarkingAllAsRead: markAllAsReadMutation.isPending,
  };
}
